import { Button, CloseButton, Drawer, Flex, Heading } from '@chakra-ui/react';
import { Select } from 'chakra-react-select';
import { convertUTCToTimezoneDate, DatePicker, formatDateUTC, Loading, toaster, useDrawer } from '@shared';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useGetAllAccounts } from '../../api/hooks/useAccountSuperadmin';
import { useAddInboundNumberReservation, useGetSingleInboundNumberReservation, useUpdateInboundNumberReservation } from '../../api/hooks/useInboundNumberReservation';
import { useGetAllInboundNumbers } from '../../api/hooks/useInboundNumberSuperadmin';

type InboundNumberForm = {
	inboundNumberID: number;
	accountID: number;
	timeStart: Date | string | null;
	timeEnd: Date | string | null;
};

export default function SuperadminInboundNumberReservationDrawer({ inboundNumberReservationID }: { inboundNumberReservationID?: number }) {
	const { t } = useTranslation();
	const { mutate: updateInboundNumber, isPending: isUpdatingInboundNumber } = useUpdateInboundNumberReservation();
	const { mutate: addInboundNumber, isPending: isAddingInboundNumber } = useAddInboundNumberReservation();

	const { closeDrawer } = useDrawer();
	const queryClient = useQueryClient();
	const { data: accounts, isFetching: isPendingAccounts } = useGetAllAccounts();
	const { data: inboundNumbers, isFetching: isFetchingInboundNumbers } = useGetAllInboundNumbers();

	const { data: inboundNumberReservation, isFetching: isFetchingInboundNumber } = useGetSingleInboundNumberReservation(inboundNumberReservationID as number, {
		isEnabled: !!inboundNumberReservationID,
	});

	const methods = useForm<InboundNumberForm>();

	const {
		setValue,
		handleSubmit,
		reset,
		control,
		formState: { dirtyFields, isDirty },
	} = methods;

	useEffect(() => {
		if (inboundNumberReservation) {
			reset({
				inboundNumberID: inboundNumberReservation.inboundNumberID,
				accountID: inboundNumberReservation.accountID,
				timeStart: inboundNumberReservation.timeStart,
				timeEnd: inboundNumberReservation.timeEnd,
			});
		}
	}, [inboundNumberReservation, reset]);

	function saveInboundNumber(values: InboundNumberForm): void {
		if (inboundNumberReservationID) {
			patchInboundNumber(values);
		} else {
			const payload: AddInboundNumberReservationRequest = {
				accountID: values.accountID,
				inboundNumberID: values.inboundNumberID,
				timeStart: values.timeStart as string,
			};

			if (values.timeEnd) {
				payload['timeEnd'] = values.timeEnd as string;
			}

			addInboundNumber(payload, {
				onSuccess: async () => {
					toaster.create({
						title: t('general.saved'),
						type: 'success',
					});
					queryClient.refetchQueries({ queryKey: ['inboundNumberReservations'] });
					queryClient.refetchQueries({ queryKey: ['inboundNumberReservation'] });

					closeDrawer('inboundNumberReservationDrawer');
				},
			});
		}
	}

	function patchInboundNumber(values: InboundNumberForm): void {
		if (!inboundNumberReservationID) {
			return;
		}

		const payload: UpdateInboundNumberReservationRequest = {
			inboundNumberReservationID: inboundNumberReservationID,
		};

		(Object.keys(dirtyFields) as Array<keyof InboundNumberForm>).forEach(key => {
			const value = values[key];

			if (key === 'timeStart') {
				payload[key] = value as string;
			}

			if (key === 'timeEnd') {
				payload[key] = value as string;
			}
		});

		updateInboundNumber(payload, {
			onSuccess: async () => {
				toaster.create({
					title: t('general.saved'),
					type: 'success',
				});
				queryClient.refetchQueries({ queryKey: ['inboundNumberReservations'] });
				queryClient.refetchQueries({ queryKey: ['inboundNumberReservation'] });

				closeDrawer('inboundNumberReservationDrawer');
			},
		});
	}

	const timeStart = useWatch({ control, name: 'timeStart' });
	const timeEnd = useWatch({ control, name: 'timeEnd' });
	const inboundNumberID = useWatch({ control, name: 'inboundNumberID' });
	const accountID = useWatch({ control, name: 'accountID' });

	if (isPendingAccounts || isFetchingInboundNumbers) {
		return <Loading />;
	}

	if (!accounts || !inboundNumbers) {
		throw new Error('Unable to fetch accounts and/or inbound numbers.');
	}

	const accountItems = accounts.data.accounts.map(account => ({
		label: account.name,
		value: account.accountID,
	}));

	const inboundNumberItems = inboundNumbers.data.inboundNumbers.map(number => {
		const accountID = number.inboundNumberReservations?.find(reservation => reservation.isActive)?.accountID;
		const account = accounts.data.accounts.find(ac => ac.accountID === accountID)?.name;

		return {
			label: `${number.phone} - ${account ? t('general.is_active', { account }) : t('general.is_not_active')}`,
			value: number.inboundNumberID,
		};
	});

	return isFetchingInboundNumber ? (
		<Loading />
	) : (
		<FormProvider {...methods}>
			<form style={{ height: '100%', display: 'flex', flexDirection: 'column' }} onSubmit={handleSubmit(saveInboundNumber)}>
				<Drawer.Header>
					<Drawer.Title marginRight="20px" wordBreak={'break-word'}>
						{inboundNumberReservationID ? t('general.edit_inbound_number_reservation') : t('general.add_inbound_number_reservation')}
					</Drawer.Title>
				</Drawer.Header>
				<Drawer.Body flex={1}>
					<Flex flexDirection={'column'} gap={4} marginBottom={3} width={'100%'}>
						<Flex direction={'column'} gap={2}>
							<Heading size="sm">{t('general.inbound_number')}</Heading>
							<Select
								required
								isDisabled={!!inboundNumberReservationID}
								options={inboundNumberItems}
								value={inboundNumberItems.find(item => item.value === inboundNumberID) ?? null}
								onChange={e => setValue('inboundNumberID', e?.value as number, { shouldDirty: true })}
							/>
						</Flex>
						<Flex direction={'column'} gap={2}>
							<Heading size="sm">{t('general.account')}</Heading>
							<Select
								required
								isDisabled={!!inboundNumberReservationID}
								options={accountItems}
								value={accountItems.find(item => item.value === accountID) ?? null}
								onChange={e => setValue('accountID', e?.value as number, { shouldDirty: true })}
							/>
						</Flex>
						<Flex direction={'column'} gap={2}>
							<Heading size="sm">{t('general.time_active_start')}</Heading>
							<DatePicker
								required={Object.keys(dirtyFields).includes('accountID')}
								timeFormat="HH:mm"
								dateFormat="dd.MM.yyyy - HH:mm"
								placeholderText={t('general.click_to_select_date')}
								showTimeSelect
								selected={timeStart ? convertUTCToTimezoneDate(timeStart as string) : null}
								onChange={(date: Date | null) => {
									setValue('timeStart', date instanceof Date ? formatDateUTC(date) : null, {
										shouldDirty: true,
									});
								}}
							/>
						</Flex>
						<Flex direction={'column'} gap={2}>
							<Heading size="sm">{t('general.time_active_end')}</Heading>
							<DatePicker
								timeFormat="HH:mm"
								dateFormat="dd.MM.yyyy - HH:mm"
								placeholderText={t('general.click_to_select_date')}
								showTimeSelect
								selected={timeEnd ? convertUTCToTimezoneDate(timeEnd as string) : null}
								onChange={(date: Date | null) => {
									setValue('timeEnd', date instanceof Date ? formatDateUTC(date) : null, {
										shouldDirty: true,
									});
								}}
							/>
						</Flex>
					</Flex>
				</Drawer.Body>
				<Drawer.Footer>
					<Button onClick={() => closeDrawer('inboundNumberReservationDrawer')} variant="outline">
						{t('general.cancel')}
					</Button>
					<Button type="submit" disabled={isUpdatingInboundNumber || isAddingInboundNumber || !isDirty}>
						{isUpdatingInboundNumber ? t('general.please_wait') : t('general.save')}
					</Button>
				</Drawer.Footer>

				<Drawer.CloseTrigger asChild>
					<CloseButton size="sm" />
				</Drawer.CloseTrigger>
			</form>
		</FormProvider>
	);
}
